import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, Empty, Button, Popconfirm, message } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { fetchMarket, sendToTrucks, extendLoad } from "../../utils/matchingApi";
import { ORDER_CREATED } from "../constants/status";
import "./matching.scss";

/**
 * Post-publish market view (spec §5.2): how many free trucks match, how many
 * are fresh, average score, and the TOP trucks list. Polls every 5 seconds
 * while the load is open. Renders nothing if the matching service is not
 * reachable or the user may not see this load.
 */
const REFRESH_MS = 5000;

const SCORE_LABELS = {
  location: "Локация",
  direction: "Направление",
  bodyType: "Кузов",
  availability: "Дата",
  capacity: "Грузоподъёмность",
};

function TruckDetails({ record }) {
  const rows = [
    ["Водитель", record.driver_name || `машина #${record.vehicle_id}`],
    ["Машина", [record.mark, record.government_number].filter(Boolean).join(" · ") || "—"],
    [
      "Кузов / вместимость",
      [
        record.body_type_name,
        record.max_load_weight ? `${record.max_load_weight} т` : null,
        record.max_load_volume ? `${record.max_load_volume} м³` : null,
      ]
        .filter(Boolean)
        .join(" · ") || "—",
    ],
    ["Где сейчас", record.city + (record.distance_km != null ? ` (~${Math.round(record.distance_km)} км до загрузки)` : "")],
    ["Куда ищет", (record.destinations || []).map((d) => d.city).join(", ") || "Любое"],
    ["Радиус подачи", `${record.radius_km} км`],
    ["Свободна с", record.available_from ? new Date(record.available_from).toLocaleDateString("ru-RU") : "—"],
    ["Свежесть", `подтверждено ${record.confirmed_minutes_ago} мин назад${record.fresh ? " (свежая)" : ""}`],
  ];
  return (
    <div style={{ padding: "4px 8px" }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: "flex", gap: 12, fontSize: 13, lineHeight: "22px" }}>
          <span style={{ color: "#8c8c8c", minWidth: 150 }}>{label}:</span>
          <span>{value}</span>
        </div>
      ))}
      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        {Object.entries(record.score_components || {}).map(([k, v]) => (
          <Tag key={k} color={k === "location" ? "blue" : "default"}>
            {SCORE_LABELS[k] || k}: {v}
          </Tag>
        ))}
      </div>
    </div>
  );
}

const MarketPanel = ({ orderId, orderStatus }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const timerRef = useRef(null);

  const selectedCount = selectedKeys.length;

  const onSend = async () => {
    setSending(true);
    try {
      const res = await sendToTrucks(
        orderId,
        selectedCount > 0 ? { match_ids: selectedKeys } : {},
      );
      const skipped = res.skipped_details?.length
        ? `, пропущено ${res.skipped_details.length} (машина уже не «Ищу груз»)`
        : "";
      message.success(
        selectedCount > 0
          ? `Заявка отправлена выбранным: ${res.sent}${skipped}`
          : `Заявка отправлена ${res.sent} ${res.sent === 1 ? "водителю" : "водителям"} (score ≥ ${res.threshold})`,
      );
      setSelectedKeys([]);
      load();
    } catch (err) {
      message.error(err?.message || "Не удалось отправить заявку");
    } finally {
      setSending(false);
    }
  };

  const isOpen = orderStatus === ORDER_CREATED;

  const load = async () => {
    if (!orderId || !isOpen) {
      return;
    }
    try {
      const market = await fetchMarket(orderId);
      setData(market);
      setHidden(false);
    } catch (err) {
      // 403 → not the owner; other errors → service unavailable; hide panel
      setHidden(true);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    const onRefresh = () => load();
    window.addEventListener("itruck:matching-refresh", onRefresh);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("itruck:matching-refresh", onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderStatus]);

  if (hidden || !isOpen || !data) {
    return null;
  }

  const m = data.market || {};

  const columns = [
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      width: 80,
      render: (score) => (
        <Tag color={score >= 85 ? "green" : score >= 70 ? "blue" : "orange"}>
          {score}
        </Tag>
      ),
    },
    {
      title: "Водитель",
      dataIndex: "driver_name",
      key: "driver",
      render: (v, r) => v || `#${r.vehicle_id}`,
    },
    {
      title: "Машина",
      key: "vehicle",
      render: (_, r) =>
        [r.body_type_name, r.max_load_weight ? `${r.max_load_weight}т` : null]
          .filter(Boolean)
          .join(" · ") || `#${r.vehicle_id}`,
    },
    {
      title: "Где сейчас",
      key: "city",
      render: (_, r) => (
        <span>
          {r.city}
          {r.distance_km != null && (
            <span className="matching-muted"> · ~{Math.round(r.distance_km)} км</span>
          )}
        </span>
      ),
    },
    {
      title: "Куда ищет",
      key: "destinations",
      render: (_, r) =>
        r.destinations && r.destinations.length > 0
          ? r.destinations.map((d) => d.city).join(", ")
          : "Любое",
    },
    {
      title: "Свободна с",
      dataIndex: "available_from",
      key: "available_from",
      render: (v) => (v ? new Date(v).toLocaleDateString("ru-RU") : "—"),
    },
    {
      title: "Свежесть",
      key: "fresh",
      render: (_, r) =>
        r.fresh ? (
          <Tag color="green">подтверждено {r.confirmed_minutes_ago} мин назад</Tag>
        ) : (
          <Tag>подтверждено {r.confirmed_minutes_ago} мин назад</Tag>
        ),
    },
  ];

  return (
    <div className="matching-panel">
      <div className="order-form__section__title">
        <span className="order-form__section__title__text">
          Рынок: подходящий транспорт
        </span>
      </div>
      {data.responses && (
        <div
          className="matching-responses"
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}
        >
          <Tag>Отправлено: {data.responses.sent}</Tag>
          <Tag>Просмотрено: {data.responses.viewed}</Tag>
          <Tag color="green">Откликнулись: {data.responses.responded}</Tag>
          <Tag color="blue">Предложили цену: {data.responses.countered}</Tag>
        </div>
      )}
      {data.load?.expires_in_minutes != null && (
        <div style={{ marginBottom: 10, fontSize: 13, color: "#374151" }}>
          Груз актуален ещё{" "}
          {data.load.expires_in_minutes >= 60
            ? `${Math.floor(data.load.expires_in_minutes / 60)} ч ${data.load.expires_in_minutes % 60} мин`
            : `${data.load.expires_in_minutes} мин`}
          {"  "}
          <Button
            size="small"
            onClick={async () => {
              try {
                await extendLoad(orderId);
                message.success("Срок актуальности груза продлён");
                load();
              } catch (err) {
                message.error(err?.message || "Не удалось продлить");
              }
            }}
          >
            Груз ещё актуален
          </Button>
        </div>
      )}
      <div className="matching-stats">
        <div className="matching-stat">
          <div className="matching-stat__value">{m.matched_trucks ?? 0}</div>
          <div className="matching-stat__label">
            подходящих машин (score ≥ {m.matched_above_threshold ?? 70})
          </div>
        </div>
        <div className="matching-stat">
          <div className="matching-stat__value">{m.fresh_trucks ?? 0}</div>
          <div className="matching-stat__label">
            активны недавно (≤ {m.fresh_within_minutes ?? 120} мин)
          </div>
        </div>
        <div className="matching-stat">
          <div className="matching-stat__value">{m.avg_score ?? "—"}</div>
          <div className="matching-stat__label">средний Match Score</div>
        </div>
      </div>
      {m.geo_buckets && (m.matched_trucks ?? 0) > 0 && (
        <div className="matching-geo" style={{ marginBottom: 10, fontSize: 13, color: "#374151" }}>
          <strong>Найдено {m.matched_trucks}:</strong>{" "}
          {[
            m.geo_buckets.same_city > 0 && `${m.geo_buckets.same_city} — в городе загрузки`,
            m.geo_buckets.within_50 > 0 && `${m.geo_buckets.within_50} — до 50 км`,
            m.geo_buckets.within_100 > 0 && `${m.geo_buckets.within_100} — до 100 км`,
            m.geo_buckets.within_200 > 0 && `${m.geo_buckets.within_200} — до 200 км`,
            m.geo_buckets.farther > 0 && `${m.geo_buckets.farther} — дальше 200 км`,
          ]
            .filter(Boolean)
            .join(", ")}
        </div>
      )}
      {m.load_ttl_expired && (
        <div className="matching-warn">
          Срок актуальности груза истёк — подтвердите груз или поднимите заново.
        </div>
      )}
      <div style={{ margin: "12px 0", display: "flex", gap: 12, alignItems: "center" }}>
        <Popconfirm
          title={
            selectedCount > 0
              ? `Отправить выбранным (${selectedCount})?`
              : "Отправить груз всем подходящим машинам?"
          }
          description={
            selectedCount > 0
              ? "Push-уведомление получат только отмеченные водители."
              : `Push-уведомление получат водители машин с score ≥ ${m.matched_above_threshold ?? 70}. Отметьте машины в списке, чтобы выбрать конкретных.`
          }
          onConfirm={onSend}
          okText="Отправить"
          cancelText="Отмена"
          disabled={(m.matched_trucks ?? 0) === 0}
        >
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            disabled={(m.matched_trucks ?? 0) === 0}
          >
            {selectedCount > 0
              ? `Отправить выбранным (${selectedCount})`
              : `Отправить всем подходящим (${m.matched_trucks ?? 0})`}
          </Button>
        </Popconfirm>
        {selectedCount > 0 && (
          <Button type="text" onClick={() => setSelectedKeys([])}>
            Снять выделение
          </Button>
        )}
      </div>
      {(m.top_trucks || []).length > 0 ? (
        <Table
          size="small"
          rowKey={(r) => r.match_id}
          columns={columns}
          dataSource={m.top_trucks}
          pagination={false}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: setSelectedKeys,
          }}
          expandable={{
            expandedRowRender: (record) => <TruckDetails record={record} />,
            rowExpandable: (record) => true,
          }}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Пока нет подходящих машин. Как только появятся — список обновится сам."
        />
      )}
      <div className="matching-muted matching-panel__note">
        Обновляется автоматически каждые 5 секунд. Отправка груза подходящим
        машинам — по кнопке «Предложить груз» на карте или через пуш-уведомления
        водителям.
      </div>
    </div>
  );
};

export default MarketPanel;
